import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "utils/api";
import Pagination from "components/pagination";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import {
  MdArrowBack,
  MdPerson,
  MdEmail,
  MdPhone,
  MdDeliveryDining,
  MdReceipt,
  MdLocationOn,
} from "react-icons/md";

if (typeof L !== "undefined" && L.Icon && L.Icon.Default) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });
}

function FitRunner({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position && position.length === 2) map.setView(position, 14);
  }, [map, position]);
  return null;
}

function createRunnerMarkerIcon(runnerName) {
  const label = runnerName || "Runner";
  const safe = String(label).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return L.divIcon({
    className: "runner-marker-wrapper",
    html: `<div class="runner-marker-pin"><span class="runner-marker-pulse"></span><span class="runner-marker-dot"></span><span class="runner-marker-label">${safe}</span></div>`,
    iconSize: [48, 56],
    iconAnchor: [24, 28],
  });
}

const ORDERS_PAGE_SIZE = 10;

const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
    <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const InfoCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-600">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p className="truncate text-sm font-semibold text-gray-900">{value ?? "—"}</p>
      </div>
    </div>
  </div>
);

export default function RunnerDetailsView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [runner, setRunner] = useState(null);
  const [orders, setOrders] = useState([]);
  const [runnerLocation, setRunnerLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordersPage, setOrdersPage] = useState(1);

  const runnerIdStr = id ? String(id) : null;
  const assignedOrders = (orders || []).filter(
    (o) => o.assigned_to && String(o.assigned_to) === runnerIdStr
  );
  const ordersTotalPages = Math.max(1, Math.ceil(assignedOrders.length / ORDERS_PAGE_SIZE));
  const paginatedOrders = assignedOrders.slice(
    (ordersPage - 1) * ORDERS_PAGE_SIZE,
    ordersPage * ORDERS_PAGE_SIZE
  );

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const [userRes, ordersRes] = await Promise.all([
          api.post("/list_single_user", { user_id: id }),
          api.get("/list_order").catch(() => ({ data: [] })),
        ]);
        const userData = userRes.data;
        setRunner(Array.isArray(userData) ? userData[0] : userData);
        setOrders(ordersRes.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load runner details");
        setRunner(null);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Poll runner live location for map
  useEffect(() => {
    if (!id) return;
    const fetchLoc = async () => {
      try {
        const res = await api.get(`/admin/runner/${id}/location`);
        if (res.data?.latitude != null && res.data?.longitude != null) {
          setRunnerLocation({
            lat: res.data.latitude,
            lon: res.data.longitude,
            last_updated: res.data.last_updated,
          });
        } else {
          setRunnerLocation(null);
        }
      } catch {
        setRunnerLocation(null);
      }
    };
    fetchLoc();
    const interval = setInterval(fetchLoc, 15000);
    return () => clearInterval(interval);
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          <p className="text-sm font-medium text-gray-500">Loading runner...</p>
        </div>
      </div>
    );
  }

  if (!runner) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-800">Runner not found.</p>
          <button
            onClick={() => navigate("/admin/runner-boys")}
            className="mt-3 text-sm font-medium text-red-600 underline hover:no-underline"
          >
            Back to Runner Boys
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/runner-boys")}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
            aria-label="Back to Runner Boys"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Runner Profile</h1>
            <p className="text-sm text-gray-500">{runner.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${
              runner.status === 1
                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : "bg-rose-100 text-rose-800 border-rose-200"
            }`}
          >
            {runner.status === 1 ? "Active" : "Inactive"}
          </span>
          <button
            onClick={() => navigate(`/admin/edit_runner/${runner.id}`)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            Edit Runner
          </button>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InfoCard label="Name" value={runner.name} icon={MdPerson} />
        <InfoCard label="Email" value={runner.email} icon={MdEmail} />
        <InfoCard label="Phone" value={runner.mobile_no} icon={MdPhone} />
      </div>

      {/* Total assigned + Assigned Orders section */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <MdDeliveryDining className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Assigned Orders
              </p>
              <p className="text-2xl font-bold text-gray-900">{assignedOrders.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Orders table */}
      <SectionCard title="Assigned Orders" icon={MdReceipt}>
        {assignedOrders.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            No orders assigned to this runner yet.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="py-3 px-2">Order ID</th>
                    <th className="py-3 px-2">Customer</th>
                    <th className="py-3 px-2">Service</th>
                    <th className="py-3 px-2">Pickup</th>
                    <th className="py-3 px-2">Drop</th>
                    <th className="py-3 px-2">Status</th>
                    <th className="py-3 px-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((o) => (
                    <tr key={o.id} className="border-b hover:bg-gray-50">
                      <td className="py-2.5 px-2 font-medium text-gray-900">
                        {o.ref_id || `#${o.id}`}
                      </td>
                      <td className="py-2.5 px-2 text-gray-600">
                        {o.users_details?.name ?? "—"}
                      </td>
                      <td className="py-2.5 px-2 text-gray-600">
                        {o.booking_service_details?.[0]?.service_details?.name ?? "—"}
                      </td>
                      <td className="py-2.5 px-2">
                        <span className="text-gray-800">{o.pickup_date}</span>
                        <span className="block text-xs text-gray-500">{o.pickup_time}</span>
                      </td>
                      <td className="py-2.5 px-2">
                        <span className="text-gray-800">{o.drop_date}</span>
                        <span className="block text-xs text-gray-500">{o.drop_time}</span>
                      </td>
                      <td className="py-2.5 px-2">
                        {(() => {
                          const st = {
                            "Pending": "bg-amber-100 text-amber-800",
                            "Confirmed": "bg-teal-100 text-teal-800",
                            "Assigned": "bg-blue-100 text-blue-800",
                            "Picked Up": "bg-cyan-100 text-cyan-800",
                            "In Processing": "bg-violet-100 text-violet-800",
                            "Out for Delivery": "bg-indigo-100 text-indigo-800",
                            "Delivered": "bg-emerald-100 text-emerald-800",
                            "Cancelled": "bg-red-100 text-red-800",
                            "Cancelled by Customer": "bg-red-100 text-red-800",
                          };
                          return (
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${st[o.status] || "bg-gray-100 text-gray-800"}`}>
                              {o.status ?? "—"}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <button
                          onClick={() => navigate(`/admin/orders/${o.id}`)}
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ordersTotalPages > 1 && (
              <Pagination
                currentPage={ordersPage}
                totalPages={ordersTotalPages}
                totalItems={assignedOrders.length}
                pageSize={ORDERS_PAGE_SIZE}
                onPageChange={setOrdersPage}
                itemName="orders"
              />
            )}
          </>
        )}
      </SectionCard>

      {/* Runner live location map */}
      <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 overflow-hidden shadow-lg ring-1 ring-black/5">
        <style>{`
          .runner-marker-wrapper { background: none !important; border: none !important; }
          .runner-marker-pin { position: relative; display: flex; flex-direction: column; align-items: center; }
          .runner-marker-pulse { position: absolute; width: 24px; height: 24px; border-radius: 50%; background: rgba(34, 197, 94, 0.5); animation: runner-pulse 1.5s ease-out infinite; top: 0; left: 50%; transform: translateX(-50%); }
          .runner-marker-dot { position: relative; width: 16px; height: 16px; border-radius: 50%; background: #22c55e; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); z-index: 1; }
          .runner-marker-label { margin-top: 4px; font-size: 11px; font-weight: 700; color: #166534; background: rgba(255,255,255,0.95); padding: 2px 8px; border-radius: 10px; box-shadow: 0 1px 4px rgba(0,0,0,0.2); white-space: nowrap; }
          @keyframes runner-pulse { 0% { transform: translateX(-50%) scale(0.8); opacity: 1; } 100% { transform: translateX(-50%) scale(2.2); opacity: 0; } }
        `}</style>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-white/10 bg-gradient-to-r from-gray-50 to-emerald-50/50 dark:from-white/5 dark:to-emerald-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <MdLocationOn className="h-4 w-4 text-emerald-500" />
            <span className="font-semibold">Runner live location</span>
          </div>
          {runnerLocation?.last_updated && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium bg-emerald-100 dark:bg-emerald-500/20 px-2 py-1 rounded-full">
              Live · Updated {new Date(runnerLocation.last_updated).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
        {runnerLocation?.lat != null && runnerLocation?.lon != null ? (
          <div className="h-[340px] w-full">
            <MapContainer
              center={[Number(runnerLocation.lat), Number(runnerLocation.lon)]}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
              className="rounded-b-2xl z-0"
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CartoDB'
              />
              <Marker
                position={[Number(runnerLocation.lat), Number(runnerLocation.lon)]}
                icon={createRunnerMarkerIcon(runner.name)}
              >
                <Popup>Live location</Popup>
              </Marker>
              <FitRunner position={[Number(runnerLocation.lat), Number(runnerLocation.lon)]} />
            </MapContainer>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400 px-4">
            Location will appear here when the runner app is open and sending location updates.
          </p>
        )}
      </div>
    </div>
  );
}
